/**
 * Stier der bygges af data, og som derfor kan bygges forkert to steder.
 */

/**
 * Kundens side. CVR i adresselinjen, fordi det er det nummer controllerne genkender og kan
 * dele - "/kunder/31428765" siger hvem det er, hvor et UUID ikke gør.
 *
 * Falder tilbage til id'et, når kunden ikke har et CVR. `cvr` er nullable i skemaet: en
 * udenlandsk kunde har ikke et. API'et slår begge former op, så et link med UUID virker
 * uændret - se `customerKey` i routes/customers.ts.
 */
export function customerPath(customer: { id: string; cvr: string | null }): string {
  return `/kunder/${customer.cvr ?? customer.id}`;
}

/**
 * Kontraktens side. Kontraktnummeret i adresselinjen — "/kontrakter/ONC-1078" er det, en
 * controller kan læse, sige i telefonen og genkende i Dynamics; opportunity'ens GUID er ikke.
 *
 * Samme afvejning som `customerPath`: nummeret kan ikke stå alene, fordi det ikke er udfyldt
 * på alle aftaler, og en kontrakt fra databasen har det slet ikke. Så falder stien tilbage
 * til id'et. API'et slår begge former op — se `findByKey` i demo/live-contracts.ts.
 *
 * Nummeret kodes, fordi det er fri tekst i Dynamics: står der et mellemrum eller en skråstreg
 * i det, ville en rå streng knække stien.
 */
export function contractPath(contract: { id: string; contract_no?: string | null }): string {
  const key = contract.contract_no?.trim();
  return `/kontrakter/${key ? encodeURIComponent(key) : contract.id}`;
}

/**
 * Fakturaens side. Fakturanummeret i adresselinjen — "/fakturering/F0509" er det, en controller
 * genkender fra Business Central og kan slå op derinde; BC's GUID er ikke.
 *
 * Rækkefølgen er **den samme som listen viser** i sin nummerkolonne (se Invoices.tsx):
 * `bc_invoice_id` er BC's fakturanummer, `invoice_no_proposal` er nummeret på en faktura
 * Codepass selv har dannet. Er ingen af dem udfyldt, falder stien tilbage til id'et — ellers
 * kunne en kladde uden nummer slet ikke åbnes. API'et slår begge former op, så et gemt link med
 * GUID virker uændret — se `invoiceFilter` i integrations/bc-invoices.ts.
 */
export function invoicePath(invoice: {
  id: string;
  bc_invoice_id?: string | null;
  invoice_no_proposal?: string | null;
}): string {
  const key = invoice.bc_invoice_id?.trim() || invoice.invoice_no_proposal?.trim();
  return `/fakturering/${key ? encodeURIComponent(key) : invoice.id}`;
}
